import { useState } from 'react';

import { CheckIcon, ShieldIcon, TimesIcon } from 'fluxo-ui/icons';

import { USER_ROLES } from '../../common/constants';

interface Permission {
    id: string;
    name: string;
    description: string;
    category: string;
}

interface Role {
    id: string;
    name: string;
    description: string;
    permissions: string[];
    userCount: number;
    isCustom: boolean;
}

interface RoleManagementProps {
    roles?: Role[];
    permissions?: Permission[];
    userCountsByRole?: Record<string, number>;
}

const DEFAULT_ROLES: Role[] = [
    {
        id: USER_ROLES.ORG_ADMIN,
        name: 'Organization Admin',
        description: 'Full access to all organization resources and settings',
        permissions: ['*'],
        userCount: 0,
        isCustom: false,
    },
    {
        id: USER_ROLES.ORG_MODERATOR,
        name: 'Moderator',
        description: 'Can review and approve content, grade exams',
        permissions: ['view_users', 'view_exams', 'view_questions', 'approve_questions', 'grade_exams', 'view_analytics'],
        userCount: 0,
        isCustom: false,
    },
    {
        id: USER_ROLES.ORG_INSTRUCTOR,
        name: 'Instructor',
        description: 'Can create and manage exams and questions',
        permissions: ['create_exams', 'edit_own_exams', 'create_questions', 'edit_own_questions', 'view_own_results', 'view_analytics'],
        userCount: 0,
        isCustom: false,
    },
    {
        id: USER_ROLES.ORG_MEMBER,
        name: 'Member',
        description: 'Can take exams and view own results',
        permissions: ['take_exams', 'view_own_results'],
        userCount: 0,
        isCustom: false,
    },
];

const RoleManagement: React.FC<RoleManagementProps> = ({
    roles,
    userCountsByRole = {},
    permissions = [
        {
            id: 'create_exams',
            name: 'Create Exams',
            description: 'Create new exams',
            category: 'Exams',
        },
        {
            id: 'edit_own_exams',
            name: 'Edit Own Exams',
            description: 'Edit exams created by self',
            category: 'Exams',
        },
        {
            id: 'edit_all_exams',
            name: 'Edit All Exams',
            description: 'Edit any exam in the organization',
            category: 'Exams',
        },
        {
            id: 'delete_exams',
            name: 'Delete Exams',
            description: 'Delete exams',
            category: 'Exams',
        },
        {
            id: 'view_exams',
            name: 'View All Exams',
            description: 'View all exams in organization',
            category: 'Exams',
        },
        {
            id: 'create_questions',
            name: 'Create Questions',
            description: 'Create new questions',
            category: 'Questions',
        },
        {
            id: 'edit_own_questions',
            name: 'Edit Own Questions',
            description: 'Edit questions created by self',
            category: 'Questions',
        },
        {
            id: 'edit_all_questions',
            name: 'Edit All Questions',
            description: 'Edit any question',
            category: 'Questions',
        },
        {
            id: 'delete_questions',
            name: 'Delete Questions',
            description: 'Delete questions',
            category: 'Questions',
        },
        {
            id: 'approve_questions',
            name: 'Approve Questions',
            description: 'Approve or reject questions',
            category: 'Questions',
        },
        {
            id: 'view_questions',
            name: 'View All Questions',
            description: 'View all questions in organization',
            category: 'Questions',
        },
        {
            id: 'take_exams',
            name: 'Take Exams',
            description: 'Attempt exams',
            category: 'Attempts',
        },
        {
            id: 'grade_exams',
            name: 'Grade Exams',
            description: 'Grade subjective questions',
            category: 'Grading',
        },
        {
            id: 'view_own_results',
            name: 'View Own Results',
            description: 'View own exam results',
            category: 'Results',
        },
        {
            id: 'view_all_results',
            name: 'View All Results',
            description: 'View all exam results',
            category: 'Results',
        },
        {
            id: 'view_analytics',
            name: 'View Analytics',
            description: 'Access analytics and reports',
            category: 'Analytics',
        },
        {
            id: 'manage_users',
            name: 'Manage Users',
            description: 'Add, remove, and manage users',
            category: 'Users',
        },
        {
            id: 'view_users',
            name: 'View Users',
            description: 'View user list',
            category: 'Users',
        },
        {
            id: 'manage_organization',
            name: 'Manage Organization',
            description: 'Edit organization settings',
            category: 'Organization',
        },
    ],
}) => {
    const resolvedRoles = (roles || DEFAULT_ROLES).map((role) => ({
        ...role,
        userCount: userCountsByRole[role.id] ?? role.userCount,
    }));
    const [selectedRole, setSelectedRole] = useState<Role | null>(resolvedRoles[0]);

    const hasPermission = (rolePermissions: string[], permissionId: string) => {
        return rolePermissions.includes('*') || rolePermissions.includes(permissionId);
    };

    const groupedPermissions = permissions.reduce(
        (acc, permission) => {
            if (!acc[permission.category]) {
                acc[permission.category] = [];
            }
            acc[permission.category].push(permission);
            return acc;
        },
        {} as Record<string, Permission[]>,
    );

    const getRoleColor = (roleId: string) => {
        const colors: Record<string, string> = {
            [USER_ROLES.ORG_ADMIN]: 'border-purple-500 bg-purple-50 dark:bg-purple-900/20',
            [USER_ROLES.ORG_MODERATOR]: 'border-teal-500 bg-teal-50 dark:bg-teal-900/20',
            [USER_ROLES.ORG_INSTRUCTOR]: 'border-green-500 bg-green-50 dark:bg-green-900/20',
            [USER_ROLES.ORG_MEMBER]: 'border-slate-500 bg-slate-50 dark:bg-slate-700',
        };
        return colors[roleId] || colors[USER_ROLES.ORG_MEMBER];
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Role Management</h2>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                    View and understand organization roles and their permissions
                </p>
            </div>

            {/* Info Message */}
            <div className="p-4 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg">
                <p className="text-sm text-teal-800 dark:text-teal-200">
                    💡 These are the predefined roles for your organization. Each role has specific permissions that define what users can
                    do in the system.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Roles List */}
                <div className="lg:col-span-1 space-y-3">
                    {resolvedRoles.map((role) => (
                        <button
                            key={role.id}
                            onClick={() => setSelectedRole(role)}
                            className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                                selectedRole?.id === role.id
                                    ? getRoleColor(role.id)
                                    : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600'
                            }`}
                        >
                            <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center gap-2">
                                    <ShieldIcon className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                                    <h3 className="font-semibold text-slate-900 dark:text-white">{role.name}</h3>
                                </div>
                                {role.permissions.includes('*') && (
                                    <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs rounded">
                                        Admin
                                    </span>
                                )}
                            </div>
                            <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">{role.description}</p>
                            <div className="flex items-center justify-between text-xs">
                                <span className="text-slate-500 dark:text-slate-500">
                                    {role.userCount} user{role.userCount !== 1 ? 's' : ''}
                                </span>
                                {!role.isCustom && <span className="text-slate-500 dark:text-slate-500">System Role</span>}
                            </div>
                        </button>
                    ))}
                </div>

                {/* Permissions Matrix */}
                <div className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                    {selectedRole ? (
                        <div className="p-6">
                            <div className="mb-6">
                                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">{selectedRole.name}</h3>
                                <p className="text-sm text-slate-600 dark:text-slate-400">{selectedRole.description}</p>
                                {selectedRole.permissions.includes('*') && (
                                    <div className="mt-3 p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                                        <p className="text-sm text-purple-800 dark:text-purple-200">
                                            ⭐ This role has full administrative access to all features and resources.
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Permissions by Category */}
                            <div className="space-y-6">
                                {Object.entries(groupedPermissions).map(([category, perms]) => (
                                    <div key={category}>
                                        <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                                            <div className="w-2 h-2 bg-teal-500 rounded-full"></div>
                                            <span>{category}</span>
                                        </h4>
                                        <div className="space-y-2">
                                            {perms.map((permission) => {
                                                const has = hasPermission(selectedRole.permissions, permission.id);
                                                return (
                                                    <div
                                                        key={permission.id}
                                                        className={`flex items-start gap-3 p-3 rounded-lg ${
                                                            has ? 'bg-green-50 dark:bg-green-900/20' : 'bg-slate-50 dark:bg-slate-700'
                                                        }`}
                                                    >
                                                        {has ? (
                                                            <CheckIcon className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                                                        ) : (
                                                            <TimesIcon className="w-5 h-5 text-slate-400 dark:text-slate-600 flex-shrink-0 mt-0.5" />
                                                        )}
                                                        <div className="flex-1">
                                                            <div
                                                                className={`text-sm font-medium ${
                                                                    has
                                                                        ? 'text-green-900 dark:text-green-100'
                                                                        : 'text-slate-500 dark:text-slate-400'
                                                                }`}
                                                            >
                                                                {permission.name}
                                                            </div>
                                                            <div
                                                                className={`text-xs ${
                                                                    has
                                                                        ? 'text-green-700 dark:text-green-300'
                                                                        : 'text-slate-400 dark:text-slate-500'
                                                                }`}
                                                            >
                                                                {permission.description}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="p-12 text-center text-slate-500 dark:text-slate-400">Select a role to view its permissions</div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RoleManagement;
